/**
 * PersonalBestWeekCard — small cream-surfaced card under the wallet hero.
 * Plan 02-11 Task 2 / UI-SPEC §5.
 *
 * "Beste uke noensinne · {{points}} p · uke {{week}}, {{year}}"
 *
 * Reads usePersonalBest({memberId}); renders nothing when the API returns
 * `personalBest: null` (member has no positive transactions yet — empty
 * wallet or fresh kid).
 *
 * weekTag parsing: backend returns `'YYYY-Www'` (Plan 02-04 personalBestWeek
 * via osloIsoWeek tz helper). We split on '-W' to get year + week.
 *
 * The "Beste uke noensinne" copy is the HIGH-1 #2 equaliser signal —
 * a kid behind on this week's leaderboard still has a tangible best-ever
 * marker to celebrate.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersonalBest } from './usePointsQueries';

interface PersonalBestWeekCardProps {
  memberId: string;
  className?: string;
}

function parseWeekTag(tag: string): { year: number; week: number } | null {
  // 'YYYY-Www' (e.g. '2026-W18')
  const m = /^(\d{4})-W(\d{1,2})$/.exec(tag);
  if (!m) return null;
  const year = Number.parseInt(m[1] ?? '', 10);
  const week = Number.parseInt(m[2] ?? '', 10);
  if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
  return { year, week };
}

export function PersonalBestWeekCard({
  memberId,
  className,
}: PersonalBestWeekCardProps): ReactNode {
  const { t } = useTranslation();
  const q = usePersonalBest(memberId);
  const data = q.data ?? null;
  if (!data) return null;
  const parsed = parseWeekTag(data.weekTag);
  if (!parsed) return null;
  return (
    <section
      data-testid="personal-best-week-card"
      className={[
        'bg-raised border-card-line rounded-lg border p-4',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="text-ink text-sm">
        {t('points.personalBest.format', {
          points: data.points,
          week: parsed.week,
          year: parsed.year,
        })}
      </p>
    </section>
  );
}
